import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonButton,
  IonIcon,
  IonCard,
  IonCardContent,
  IonSpinner,
  IonToast,
  IonModal,
  IonInput,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonToggle,
} from '@ionic/react';
import { arrowBack, eyeOutline, sparklesOutline, documentTextOutline, bookmarkOutline, bookmark, createOutline } from 'ionicons/icons';
import { useHistory, useParams } from 'react-router-dom';
import TitleBar from '../components/TitleBar.tsx';
import FitAnalysisModal from '../components/FitAnalysisModal.tsx';
import CoverLetterModal from '../components/CoverLetterModal.tsx';
import { jobs } from '../services/api.ts';
import { useAuth } from '../context/AuthContext.tsx';
import { useResponsive } from '../hooks/useResponsive.tsx';

interface ScrapedJob {
  id: number;
  user_id: string;
  job_url: string;
  title: string;
  company: string;
  location?: string;
  description?: string;
  salary_min?: number;
  salary_max?: number;
  currency?: string;
  job_type?: string;
  is_remote?: boolean;
  is_saved?: boolean;
  site?: string;
  created_at: string;
}

// Smart markdown-like formatter for job descriptions (same as JobDetailView)
const FormattedJobDescription: React.FC<{ description: string }> = ({ description }) => {
  const formatInlineText = (text: string) => {
    return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  };

  const formatText = (text: string) => {
    const paragraphs = text.split(/\n\s*\n/);

    return paragraphs.map((paragraph, index) => {
      const trimmed = paragraph.trim();
      if (!trimmed) return null;

      // Check for headers
      if (trimmed.match(/^\*\*.*\*\*$/) || (trimmed.endsWith(':') && trimmed.length < 60 && !trimmed.includes('+'))) {
        return (
          <h4 key={index} style={{
            fontWeight: 700,
            fontSize: '1.1rem',
            color: '#667eea',
            margin: '20px 0 12px 0',
            borderBottom: '2px solid #764ba2',
            paddingBottom: '4px'
          }}>
            {trimmed.replace(/\*\*/g, '').replace(/:$/, '')}
          </h4>
        );
      }

      // Check for bullet points
      if (trimmed.match(/^[\*\+]\s/)) {
        const bulletLines = trimmed.split('\n').filter(line => line.trim());
        return (
          <ul key={index} style={{
            margin: '12px 0',
            paddingLeft: '20px',
            listStyleType: 'disc'
          }}>
            {bulletLines.map((line, i) => (
              <li key={i} style={{
                margin: '8px 0',
                lineHeight: '1.5',
                color: 'var(--app-text-color)'
              }} dangerouslySetInnerHTML={{ __html: formatInlineText(line.replace(/^[\*\+]\s/, '')) }}>
              </li>
            ))}
          </ul>
        );
      }

      // Check for numbered lists
      if (trimmed.match(/^\d+\.\s/)) {
        const numberedLines = trimmed.split('\n').filter(line => line.trim());
        return (
          <ol key={index} style={{
            margin: '12px 0',
            paddingLeft: '20px'
          }}>
            {numberedLines.map((line, i) => (
              <li key={i} style={{
                margin: '8px 0',
                lineHeight: '1.5',
                color: 'var(--app-text-color)'
              }} dangerouslySetInnerHTML={{ __html: formatInlineText(line.replace(/^\d+\.\s/, '')) }}>
              </li>
            ))}
          </ol>
        );
      }

      // Regular paragraph
      return (
        <p key={index} style={{
          margin: '12px 0',
          lineHeight: '1.6',
          color: 'var(--app-text-color)'
        }} dangerouslySetInnerHTML={{ __html: formatInlineText(trimmed) }}>
        </p>
      );
    });
  };

  return <div>{formatText(description)}</div>;
};

const ScrapedJobDetail: React.FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { isMobile } = useResponsive();

  const [job, setJob] = useState<ScrapedJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFitAnalysisModal, setShowFitAnalysisModal] = useState(false);
  const [showCoverLetterModal, setShowCoverLetterModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [showUnsaveToast, setShowUnsaveToast] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    company: '',
    location: '',
    salary_min: '',
    salary_max: '',
    currency: 'USD',
    job_type: '',
    is_remote: false,
  });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  useEffect(() => {
    const loadScrapedJob = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await jobs.getScrapedJob(id);
        console.log('Loaded job:', response);
        setJob(response);
      } catch (err: any) {
        console.error('Error loading scraped job:', err);
        console.error('Error response:', err.response);
        console.error('Error data:', err.response?.data);

        if (err.response?.status === 404) {
          setError('Job not found. It may have been deleted or the link is invalid.');
        } else if (err.response?.status === 500) {
          setError('Server error. Please try again later.');
        } else if (!err.response) {
          setError('Network error. Please check your connection.');
        } else {
          const errorMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to load job details';
          setError(errorMsg);
        }
      } finally {
        setLoading(false);
      }
    };

    loadScrapedJob();
  }, [id]);

  const handleGoBack = () => {
    history.goBack();
  };

  const handleToggleSave = async () => {
    if (!job) return;

    try {
      setIsSaving(true);
      if (job.is_saved) {
        await jobs.unsaveJob(job.id);
        setJob({ ...job, is_saved: false });
        setShowUnsaveToast(true); // Show toast after successfully unsaving
      } else {
        await jobs.saveJob(job.id);
        setJob({ ...job, is_saved: true });
        setShowSaveToast(true); // Show toast after successfully saving
      }
    } catch (err: any) {
      console.error('Error toggling save:', err);
      alert(err.response?.data?.detail || 'Failed to save job');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenEditModal = () => {
    if (!job) return;
    setEditForm({
      title: job.title || '',
      company: job.company || '',
      location: job.location || '',
      salary_min: job.salary_min?.toString() || '',
      salary_max: job.salary_max?.toString() || '',
      currency: job.currency || 'USD',
      job_type: job.job_type || '',
      is_remote: job.is_remote || false,
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!job) return;

    try {
      setIsSavingEdit(true);
      const updates: any = {
        title: editForm.title,
        company: editForm.company,
        location: editForm.location || null,
        currency: editForm.currency,
        job_type: editForm.job_type || null,
        is_remote: editForm.is_remote,
      };

      // Only include salary if provided
      if (editForm.salary_min) {
        updates.salary_min = parseInt(editForm.salary_min);
      }
      if (editForm.salary_max) {
        updates.salary_max = parseInt(editForm.salary_max);
      }

      const updatedJob = await jobs.updateScrapedJob(job.id, updates);
      setJob(updatedJob);
      setShowEditModal(false);
      setShowSaveToast(true);
    } catch (err: any) {
      console.error('Error updating job:', err);
      alert(err.response?.data?.detail || 'Failed to update job');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const formatSalary = (min?: number, max?: number, currency?: string) => {
    if (!min && !max) return 'Salary not specified';
    const curr = currency || 'USD';
    if (min && max) {
      return `${curr} ${min.toLocaleString()} - ${max.toLocaleString()}`;
    }
    if (min) return `${curr} ${min.toLocaleString()}+`;
    if (max) return `Up to ${curr} ${max.toLocaleString()}`;
    return 'Salary not specified';
  };

  if (loading) {
    return (
      <IonPage>
        <TitleBar title="Job Details" />
        <IonContent>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: '200px'
          }}>
            <IonSpinner name="crescent" style={{ width: '48px', height: '48px' }} />
            <p style={{ color: 'var(--ion-color-medium)', fontSize: '16px', marginTop: '16px' }}>
              Loading job details...
            </p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (error || !job) {
    return (
      <IonPage>
        <TitleBar title="Job Details" />
        <IonContent>
          <div style={{ padding: '20px' }}>
            <IonCard style={{ '--background': '#fff5f5', border: '1px solid #feb2b2' }}>
              <IonCardContent style={{ padding: '24px', textAlign: 'center' }}>
                <p style={{ margin: 0, color: '#c53030', fontSize: '14px' }}>
                  {error || 'Job not found'}
                </p>
                <IonButton onClick={handleGoBack} style={{ marginTop: '16px' }}>
                  Go Back
                </IonButton>
              </IonCardContent>
            </IonCard>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <TitleBar title="Job Details" />
      <IonContent>
        <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
          {/* Back button */}
          <IonButton
            fill="clear"
            onClick={handleGoBack}
            style={{ marginBottom: '16px' }}
          >
            <IonIcon icon={arrowBack} slot="start" />
            Back to Jobs
          </IonButton>

          {/* Job header */}
          <IonCard style={{ marginBottom: '20px' }}>
            <IonCardContent style={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                <div style={{ flex: 1 }}>
                  <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0, color: 'var(--ion-color-dark)' }}>
                    {job.title}
                  </h1>
                  <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#667eea', margin: '8px 0 0 0' }}>
                    {job.company}
                  </h2>
                </div>
                <IonButton
                  fill="clear"
                  onClick={handleOpenEditModal}
                  style={{
                    '--color': '#667eea',
                    minWidth: 'auto',
                    marginTop: '-8px',
                  }}
                >
                  <IonIcon icon={createOutline} slot="icon-only" />
                </IonButton>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
                {job.location && (
                  <div>
                    <strong style={{ color: 'var(--ion-color-medium)', fontSize: '14px' }}>Location:</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: '16px' }}>{job.location}</p>
                  </div>
                )}

                <div>
                  <strong style={{ color: 'var(--ion-color-medium)', fontSize: '14px' }}>Salary:</strong>
                  <p style={{ margin: '4px 0 0 0', fontSize: '16px' }}>
                    {formatSalary(job.salary_min, job.salary_max, job.currency)}
                  </p>
                </div>

                {job.job_type && (
                  <div>
                    <strong style={{ color: 'var(--ion-color-medium)', fontSize: '14px' }}>Job Type:</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: '16px' }}>{job.job_type}</p>
                  </div>
                )}

                {job.is_remote !== undefined && (
                  <div>
                    <strong style={{ color: 'var(--ion-color-medium)', fontSize: '14px' }}>Remote:</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: '16px' }}>
                      {job.is_remote ? 'Yes' : 'No'}
                    </p>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '12px', flexWrap: 'wrap' }}>
                {job.job_url && (
                  <IonButton
                    href={job.job_url}
                    target="_blank"
                    fill="outline"
                  >
                    View Original Posting
                  </IonButton>
                )}
                <IonButton
                  onClick={handleToggleSave}
                  disabled={isSaving}
                  fill={job.is_saved ? "solid" : "outline"}
                  color={job.is_saved ? "success" : "medium"}
                >
                  {isSaving ? (
                    <>
                      <IonSpinner name="crescent" style={{ marginRight: '8px', width: '18px', height: '18px' }} />
                      {job.is_saved ? 'Removing...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <IonIcon icon={job.is_saved ? bookmark : bookmarkOutline} slot="start" />
                      {job.is_saved ? 'Saved' : 'Save Job'}
                    </>
                  )}
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <IonButton
              expand="block"
              onClick={() => setShowFitAnalysisModal(true)}
              style={{
                flex: '1',
                minWidth: '200px',
                height: '56px',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              <IonIcon icon={sparklesOutline} slot="start" />
              Am I a Fit?
            </IonButton>
            <IonButton
              expand="block"
              onClick={() => setShowCoverLetterModal(true)}
              color="secondary"
              style={{
                flex: '1',
                minWidth: '200px',
                height: '56px',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              <IonIcon icon={documentTextOutline} slot="start" />
              Generate Cover Letter
            </IonButton>
          </div>

          {/* Job description */}
          {job.description && (
            <IonCard>
              <IonCardContent style={{ padding: '24px' }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  marginBottom: '16px',
                  color: 'var(--ion-color-dark)'
                }}>
                  Job Description
                </h3>
                <FormattedJobDescription description={job.description} />
              </IonCardContent>
            </IonCard>
          )}
        </div>

        {/* Modals */}
        <FitAnalysisModal
          isOpen={showFitAnalysisModal}
          onClose={() => setShowFitAnalysisModal(false)}
          jobId={id}
          resumeId={user?.default_resume_id}
          isScrapedJob={true}
        />

        <CoverLetterModal
          isOpen={showCoverLetterModal}
          onClose={() => setShowCoverLetterModal(false)}
          jobId={id}
          resumeId={user?.default_resume_id}
          isScrapedJob={true}
        />

        {/* Save Success Toast */}
        <IonToast
          isOpen={showSaveToast}
          onDidDismiss={() => setShowSaveToast(false)}
          message="Job saved successfully!"
          duration={3000}
          position="top"
          color="success"
          buttons={[
            {
              text: 'View saved jobs',
              handler: () => {
                history.push('/app/jobs/saved');
              }
            }
          ]}
        />

        {/* Unsave Success Toast */}
        <IonToast
          isOpen={showUnsaveToast}
          onDidDismiss={() => setShowUnsaveToast(false)}
          message="Job removed from saved"
          duration={3000}
          position="top"
          color="warning"
        />

        {/* Edit Job Modal */}
        <IonModal isOpen={showEditModal} onDidDismiss={() => setShowEditModal(false)}>
          <IonPage>
            <IonContent style={{
              '--padding-start': isMobile ? '16px' : '20px',
              '--padding-end': isMobile ? '16px' : '20px',
              '--padding-top': isMobile ? '16px' : '20px'
            }}>
              <div style={{ maxWidth: isMobile ? '100%' : '600px', margin: '0 auto' }}>
                {/* Simple Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: isMobile ? '20px' : '24px',
                  paddingTop: isMobile ? '12px' : '20px',
                }}>
                  <h2 style={{
                    fontSize: isMobile ? '20px' : '24px',
                    fontWeight: '700',
                    color: '#1e3a8a',
                    margin: 0,
                  }}>
                    Edit Job Details
                  </h2>
                  <IonButton
                    fill="clear"
                    onClick={() => setShowEditModal(false)}
                    style={{ '--color': '#6b7280' }}
                  >
                    <IonIcon icon={arrowBack} slot="icon-only" />
                  </IonButton>
                </div>
                {/* Title */}
                <div style={{ marginBottom: '20px' }}>
                  <IonLabel style={{ fontSize: '14px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '8px' }}>
                    Job Title *
                  </IonLabel>
                  <IonInput
                    value={editForm.title}
                    onIonInput={(e) => setEditForm({ ...editForm, title: e.detail.value || '' })}
                    placeholder="e.g., Senior Software Engineer"
                    style={{
                      '--border-color': '#e5e7eb',
                      '--border-radius': '8px',
                      '--padding-start': '12px',
                      '--background': '#f9fafb',
                    }}
                  />
                </div>

                {/* Company */}
                <div style={{ marginBottom: '20px' }}>
                  <IonLabel style={{ fontSize: '14px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '8px' }}>
                    Company *
                  </IonLabel>
                  <IonInput
                    value={editForm.company}
                    onIonInput={(e) => setEditForm({ ...editForm, company: e.detail.value || '' })}
                    placeholder="e.g., Google, Microsoft"
                    style={{
                      '--border-color': '#e5e7eb',
                      '--border-radius': '8px',
                      '--padding-start': '12px',
                      '--background': '#f9fafb',
                    }}
                  />
                </div>

                {/* Location */}
                <div style={{ marginBottom: '20px' }}>
                  <IonLabel style={{ fontSize: '14px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '8px' }}>
                    Location
                  </IonLabel>
                  <IonInput
                    value={editForm.is_remote ? 'Remote' : editForm.location}
                    onIonInput={(e) => {
                      if (!editForm.is_remote) {
                        setEditForm({ ...editForm, location: e.detail.value || '' });
                      }
                    }}
                    disabled={editForm.is_remote}
                    placeholder="e.g., New York, NY"
                    style={{
                      '--border-color': '#e5e7eb',
                      '--border-radius': '8px',
                      '--padding-start': '12px',
                      '--background': editForm.is_remote ? '#e5e7eb' : '#f9fafb',
                    }}
                  />
                </div>

                {/* Remote Toggle */}
                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                  <IonLabel style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Remote Position
                  </IonLabel>
                  <IonToggle
                    checked={editForm.is_remote}
                    onIonChange={(e) => {
                      const isRemote = e.detail.checked;
                      setEditForm({
                        ...editForm,
                        is_remote: isRemote,
                        location: isRemote ? 'Remote' : editForm.location,
                      });
                    }}
                  />
                </div>

                {/* Salary Range */}
                <div style={{ marginBottom: '20px' }}>
                  <IonLabel style={{ fontSize: '14px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '8px' }}>
                    Salary Range
                  </IonLabel>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <input
                      type="number"
                      value={editForm.salary_min}
                      onChange={(e) => setEditForm({ ...editForm, salary_min: e.target.value })}
                      placeholder="Min"
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '12px',
                        background: '#f9fafb',
                        flex: 1,
                        fontSize: '14px',
                      }}
                    />
                    <input
                      type="number"
                      value={editForm.salary_max}
                      onChange={(e) => setEditForm({ ...editForm, salary_max: e.target.value })}
                      placeholder="Max"
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '12px',
                        background: '#f9fafb',
                        flex: 1,
                        fontSize: '14px',
                      }}
                    />
                    <IonSelect
                      value={editForm.currency}
                      onIonChange={(e) => setEditForm({ ...editForm, currency: e.detail.value })}
                      style={{
                        '--border-color': '#e5e7eb',
                        '--border-radius': '8px',
                        '--padding-start': '12px',
                        '--background': '#f9fafb',
                        minWidth: '100px',
                      }}
                    >
                      <IonSelectOption value="USD">USD</IonSelectOption>
                      <IonSelectOption value="EUR">EUR</IonSelectOption>
                      <IonSelectOption value="GBP">GBP</IonSelectOption>
                      <IonSelectOption value="INR">INR</IonSelectOption>
                    </IonSelect>
                  </div>
                </div>

                {/* Job Type */}
                <div style={{ marginBottom: '20px' }}>
                  <IonLabel style={{ fontSize: '14px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '8px' }}>
                    Job Type
                  </IonLabel>
                  <IonSelect
                    value={editForm.job_type}
                    onIonChange={(e) => setEditForm({ ...editForm, job_type: e.detail.value })}
                    placeholder="Select job type"
                    style={{
                      '--border-color': '#e5e7eb',
                      '--border-radius': '8px',
                      '--padding-start': '12px',
                      '--background': '#f9fafb',
                    }}
                  >
                    <IonSelectOption value="Full-time">Full-time</IonSelectOption>
                    <IonSelectOption value="Part-time">Part-time</IonSelectOption>
                    <IonSelectOption value="Contract">Contract</IonSelectOption>
                    <IonSelectOption value="Internship">Internship</IonSelectOption>
                  </IonSelect>
                </div>

                {/* Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  marginTop: '24px',
                  flexDirection: isMobile ? 'column' : 'row'
                }}>
                  <IonButton
                    expand="block"
                    fill="outline"
                    onClick={() => setShowEditModal(false)}
                    style={{ flex: isMobile ? undefined : 1 }}
                  >
                    Cancel
                  </IonButton>
                  <IonButton
                    expand="block"
                    onClick={handleSaveEdit}
                    disabled={isSavingEdit || !editForm.title || !editForm.company}
                    style={{ flex: isMobile ? undefined : 1 }}
                  >
                    {isSavingEdit ? (
                      <>
                        <IonSpinner name="crescent" style={{ marginRight: '8px', width: '18px', height: '18px' }} />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </IonButton>
                </div>
              </div>
            </IonContent>
          </IonPage>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default ScrapedJobDetail;
